import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef
} from 'react'
import PropTypes from 'prop-types'
import InputWrapper from './InputWrapper'
import { classNames, useUpdateRef, useGlobalId } from '@zambezi/sdk/common'
import { focus, componentWillReceiveProps } from './utils'

/**
 * @type {import('react').RefObject<import('./interfaces').RadioButtonRef>[]}
 */
window['__zbRadioButtons__'] = window['__zbRadioButtons__'] || []
const radioButtonsRegister = window['__zbRadioButtons__']

/**
 * Create a React Radio Button component.
 *
 * @param {import('./interfaces').RadioButtonProps} props
 * @param {import('react').Ref<import('./interfaces').RadioButtonRef>} ref
 * @returns {JSX.Element}
 */
function radioButton (props, ref) {
  const {
    withLabel,
    label,
    error,
    disabled,
    className,
    onChange,
    onFocus,
    onBlur,
    checked: propsChecked,
    id: propsId,
    defaultChecked,
    name
  } = props
  const [checked, setChecked] = useState(
    isUncontrolled(props) ? defaultChecked : propsChecked
  )
  const globalId = useGlobalId('zb-radiobutton-')
  const [id, setId] = useState(propsId || globalId)
  const [focused, setFocused] = useState(false)
  const disabledHandler = disabled ? null : noop
  const labelElementRef = useRef()
  const containerRef = useRef()
  const elementRef = useRef()
  const refProps = {
    focus: focusOptions =>
      focus(labelElementRef, elementRef, setFocused, focusOptions),
    name,
    setChecked,
    checked
  }

  useUpdateRef(containerRef, elementRef, refProps)
  useUpdateRef(ref, elementRef, refProps)
  useEffect(
    () =>
      componentWillReceiveProps(
        props,
        propsChecked,
        propsId,
        id,
        globalId,
        isUncontrolled,
        setChecked,
        setId
      ),
    [props, propsChecked, propsId, id, globalId]
  )
  useLayoutEffect(componentDidMount, [elementRef])

  const inputProps = {
    ...props,
    ref: elementRef,
    id,
    focused,
    checked,
    onMouseDown: disabledHandler && handleMouseDown,
    onClick: disabledHandler,
    onMouseUp: disabledHandler && handleMouseUp,
    onBlur: disabledHandler && handleBlur,
    onFocus: disabledHandler && handleFocus,
    onChange: disabledHandler && handleChange,
    type: 'radio',
    rootClassName: 'zb-radio-button',
    children: <span className='zb-radio-button-circle' />
  }

  return withLabel ? (
    <div
      className={classNames(
        'zb-radio-button-labelled',
        {
          'zb-radio-button-labelled-is-focused': focused,
          'zb-radio-button-labelled-is-error': error,
          'zb-radio-button-labelled-is-disabled': disabled
        },
        className
      )}
    >
      <InputWrapper {...inputProps} />
      <label
        className={'zb-radio-button-label'}
        htmlFor={id}
        id={`zb-form-elements-label-${id}`}
        ref={labelElementRef}
      >
        {label}
      </label>
    </div>
  ) : (
    <InputWrapper {...inputProps} />
  )

  function componentDidMount () {
    registerRadioButton(containerRef)
    return componentWillUnmount
  }

  function componentWillUnmount () {
    unregisterRadioButton(containerRef)
  }

  function handleMouseUp (e) {
    if (!checked) {
      handleToggle(true, e)
    }
    focus(labelElementRef, elementRef, setFocused)
  }

  function handleChange (e) {
    handleToggle(e.target.checked, e)
  }

  function handleToggle (checked, e) {
    if (disabled) return

    if (isUncontrolled(props)) {
      unCheckAllRadioButtons(name)
      setChecked(checked)
    }

    onChange(e, checked)
  }

  function handleFocus (e) {
    setFocused(true)
    onFocus(e)
  }

  function handleBlur (e) {
    setFocused(false)
    onBlur(e)
  }

  function handleMouseDown (e) {
    e.preventDefault()
  }
}

/**
 * @param {import('./interfaces').RadioButtonRef} buttonRef
 */
function registerRadioButton (buttonRef) {
  radioButtonsRegister.push(buttonRef)
}

/**
 * @param {import('./interfaces').RadioButtonRef} buttonRef
 */
function unregisterRadioButton (buttonRef) {
  const index = radioButtonsRegister.indexOf(buttonRef)
  if (index !== -1) {
    radioButtonsRegister.splice(index, 1)
  }
}

/**
 * @param {string} name
 */
function unCheckAllRadioButtons (name) {
  radioButtonsRegister.forEach(({ current: button }) => {
    if (button && button.name === name) {
      button.setChecked(false)
    }
  })
}

function isUncontrolled (props) {
  return props.checked === undefined
}

function noop () {}

const RadioButton = forwardRef(radioButton)

RadioButton.displayName = 'RadioButton'

RadioButton.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  value: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  className: PropTypes.string,
  withLabel: PropTypes.bool
}

RadioButton.defaultProps = {
  defaultChecked: false,
  disabled: false,
  error: false,
  checked: undefined,
  value: undefined,
  onChange: noop,
  onFocus: noop,
  onBlur: noop,
  withLabel: false
}

export default RadioButton
